/**
 * Learning Events API Service
 * Handles all 4W (Who-When-What-Result) learning event tracking
 */

import { apiRequest } from './apiService';
import { useAuthStore } from '../store/authStore';
import type {
  LearningEvent,
  LogLearningEventRequest,
  LogLearningEventsBatchRequest,
  LogLearningEventResponse,
  LearningEventTimelineResponse,
  LearningEventAnalyticsResponse,
  StudentProfileResponse,
  EnrollCourseRequest,
  EnrollCourseResponse,
  LearningEventFilters
} from '../types/learning';

// Use the same backend origin as authentication/admin APIs. A relative /api
// URL targets the CRA development server because this project has no dev proxy.
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const API_BASE = `${API_BASE_URL}/learning-events`;
const clientEventId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, char => {
    const value = Math.floor(Math.random() * 16);
    return (char === 'x' ? value : (value & 0x3) | 0x8).toString(16);
  });
};
const authenticatedOptions = (options: RequestInit = {}): RequestInit => {
  const token = useAuthStore.getState().token;
  return {
    ...options,
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers
    }
  };
};

/**
 * Learning Events API
 */
export const learningEventsApi = {
  /**
   * Log a single learning event
   */
  async logEvent(request: LogLearningEventRequest): Promise<LogLearningEventResponse> {
    return apiRequest<LogLearningEventResponse>(`${API_BASE}`, authenticatedOptions({
      method: 'POST',
      body: JSON.stringify({
        ...request,
        clientEventId: request.clientEventId || clientEventId()
      }),
    }));
  },

  /**
   * Log multiple learning events in batch
   */
  async logEventsBatch(request: LogLearningEventsBatchRequest): Promise<{ success: boolean; message: string; data: { count: number } }> {
    return apiRequest(`${API_BASE}/batch`, authenticatedOptions({
      method: 'POST',
      body: JSON.stringify({
        events: request.events.map(event => ({
          ...event,
          clientEventId: event.clientEventId || clientEventId()
        }))
      }),
    }));
  },

  /**
   * Get student's learning timeline
   */
  async getTimeline(filters?: LearningEventFilters): Promise<LearningEventTimelineResponse> {
    const params = new URLSearchParams();
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.offset) params.append('offset', filters.offset.toString());
    if (filters?.courseId) params.append('courseId', filters.courseId);
    if (filters?.eventType) params.append('eventType', filters.eventType);

    const queryString = params.toString();
    return apiRequest<LearningEventTimelineResponse>(
      `${API_BASE}/timeline${queryString ? `?${queryString}` : ''}`
    );
  },

  /**
   * Get learning analytics for current student
   */
  async getAnalytics(filters?: Pick<LearningEventFilters, 'startDate' | 'endDate' | 'courseId'>): Promise<LearningEventAnalyticsResponse> {
    const params = new URLSearchParams();
    if (filters?.startDate) params.append('startDate', new Date(filters.startDate).toISOString());
    if (filters?.endDate) params.append('endDate', new Date(filters.endDate).toISOString());
    if (filters?.courseId) params.append('courseId', filters.courseId);

    const queryString = params.toString();
    return apiRequest<LearningEventAnalyticsResponse>(
      `${API_BASE}/analytics${queryString ? `?${queryString}` : ''}`
    );
  },

  /**
   * Export user events as JSON
   */
  async exportEvents(filters?: LearningEventFilters): Promise<{
    success: boolean;
    message: string;
    data: {
      events: LearningEvent[];
      count: number;
      exportDate: Date;
    };
  }> {
    const params = new URLSearchParams();
    if (filters?.startDate) params.append('startDate', new Date(filters.startDate).toISOString());
    if (filters?.endDate) params.append('endDate', new Date(filters.endDate).toISOString());
    if (filters?.eventType) params.append('eventType', filters.eventType);
    if (filters?.limit) params.append('limit', filters.limit.toString());

    const queryString = params.toString();
    return apiRequest(
      `${API_BASE}/export${queryString ? `?${queryString}` : ''}`
    );
  },

  /**
   * Get student's profile with progress
   */
  async getProfile(): Promise<StudentProfileResponse> {
    return apiRequest<StudentProfileResponse>(`${API_BASE}/profile`);
  },

  /**
   * Enroll in a course
   */
  async enrollCourse(request: EnrollCourseRequest): Promise<EnrollCourseResponse> {
    return apiRequest<EnrollCourseResponse>(`${API_BASE}/profile/enroll`, {
      method: 'PUT',
      body: JSON.stringify(request),
    });
  },
};

/**
 * Helper functions for common event types
 */
export const eventHelpers = {
  /**
   * Log topic start event
   */
  async logTopicStart(topicId: string, courseId?: string, chapterId?: string): Promise<void> {
    await learningEventsApi.logEvent({
      what: {
        type: 'start_topic',
        context: { topicId, courseId, chapterId }
      }
    });
  },

  /**
   * Log topic completion event
   */
  async logTopicComplete(topicId: string, courseId?: string, timeSpent?: number): Promise<void> {
    // Compatibility argument is seconds. LearningEvent.when.duration is ms;
    // neither is foreground v1 or an input to its ledger.
    await learningEventsApi.logEvent({
      what: {
        type: 'complete_topic',
        context: { topicId, courseId }
      },
      when: { duration: timeSpent === undefined ? undefined : timeSpent * 1000 },
      result: {
        success: true,
        data: { timeSpent }
      }
    });
  },

  async logTopicCompletion(
    topicId: string,
    courseId: string | undefined,
    isCompleted: boolean,
    timeSpent = 0
  ): Promise<void> {
    await learningEventsApi.logEvent({
      what: {
        type: isCompleted ? 'complete_topic' : 'uncomplete_topic',
        context: {
          topicId,
          ...(courseId ? { courseId } : {})
        }
      },
      when: { duration: timeSpent },
      result: {
        success: true,
        data: { timeSpent }
      }
    });
  },

  /**
   * Log quiz submission event
   */
  async logQuizSubmit(
    quizId: string,
    answers: Array<{ questionId: string; answer: string; isCorrect: boolean; timeSpent: number }>,
    score: number,
    topicId?: string,
    courseId?: string
  ): Promise<void> {
    await learningEventsApi.logEvent({
      what: {
        type: 'submit_quiz',
        context: { quizId, topicId, courseId }
      },
      result: {
        success: true,
        score,
        accuracy: (answers.filter(a => a.isCorrect).length / answers.length) * 100,
        data: {}
      }
    });
  },

  /**
   * Log code execution event
   */
  async logCodeExecution(
    code: string,
    output: string,
    error?: string,
    topicId?: string,
    courseId?: string
  ): Promise<void> {
    await learningEventsApi.logEvent({
      what: {
        type: error ? 'code_error' : 'execute_code',
        context: { topicId, courseId }
      },
      result: {
        success: !error,
        data: {}
      }
    });
  },

  /**
   * Log AI help request event
   */
  async logAIHelpRequest(
    question: string,
    code?: string,
    topicId?: string,
    courseId?: string
  ): Promise<void> {
    await learningEventsApi.logEvent({
      what: {
        type: 'request_ai_help',
        context: { topicId, courseId }
      },
      result: {
        success: true,
        data: {}
      }
    });
  },

  /**
   * Log self-explanation event
   */
  async logSelfExplanation(
    topicId: string,
    cardId: string,
    explanation: string,
    understanding: 'low' | 'medium' | 'high',
    timeSpent?: number
  ): Promise<void> {
    const wordCount = explanation.split(/\s+/).length;

    await learningEventsApi.logEvent({
      what: {
        type: 'write_explanation',
        context: { topicId, cardId }
      },
      when: { duration: timeSpent },
      result: {
        success: true,
        data: {
          wordCount
        }
      }
    });
  },

  /**
   * Log session start event
   */
  async logSessionStart(sessionId: string): Promise<void> {
    await learningEventsApi.logEvent({
      what: {
        type: 'start_session',
        context: {}
      },
      sessionId
    });
  },

  /**
   * Log session end event
   */
  async logSessionEnd(sessionId: string, duration?: number): Promise<void> {
    await learningEventsApi.logEvent({
      what: {
        type: 'end_session',
        context: {}
      },
      when: { duration },
      sessionId
    });
  },

  /**
   * Record the privacy-limited authenticated SPA navigation event.
   */
  async logPageView(pathname: string, sessionId: string): Promise<void> {
    await learningEventsApi.logEvent({
      what: {
        type: 'page_view',
        context: { pathname }
      },
      sessionId
    });
  },

  /**
   * Log navigation event
   */
  async logNavigation(fromTopicId?: string, toTopicId?: string): Promise<void> {
    await learningEventsApi.logEvent({
      what: {
        type: 'navigate_to_topic',
        context: { fromTopicId, toTopicId }
      }
    });
  },

};

export default learningEventsApi;
